import { getCredential } from '@/api/auth'
import { urlSearchParams } from '@/util/urlSearchParams'
import { getLocale } from '@/locale/i18n'
import storage from '@/util/storage'

class RequestService {
  apiVersion = 'v1.5'

  async get(url, options = {}) {
    const requestOptions = await this.prepareRequest(url, { method: 'GET', ...options })
    return this.callRequest(requestOptions)
  }

  async post(url, data, options = {}) {
    const { retry, ...requestOptions } = options || {}
    const request = await this.prepareRequest(url, { method: 'POST', body: data, ...requestOptions })
    return this.callRequest(request, retry)
  }

  async patch(url, data, options = {}) {
    const request = await this.prepareRequest(url, { method: 'PATCH', body: data, ...options })
    return this.callRequest(request)
  }
  async put(url, data, options = {}) {
    const request = await this.prepareRequest(url, { method: 'PUT', body: data, ...options })
    return this.callRequest(request)
  }

  async delete(url, options = {}) {
    const request = await this.prepareRequest(url, { method: 'DELETE', ...options })
    return this.callRequest(request)
  }

  parseApiVersion(url) {
    // Regex: verifies if string starts with version, ex: v1.1, /v1.1, /v1, etc.
    if (new RegExp(/^\/?v(\d+)(\.\d+)?/).test(url) === false && !url.startsWith('/o/')) {
      return '/' + this.apiVersion + url
    }
    return url
  }

  parseFilters(options) {
    const { startIndex, stopIndex, question, ...filters } = options
    const params = new urlSearchParams(filters)

    if (startIndex !== undefined) {
      params.set('offset', startIndex)
    }

    if (stopIndex !== undefined) {
      params.set('limit', stopIndex - startIndex)
    }

    if (question) {
      if (typeof question === 'number') {
        params.set('question', question)
      } else if (question.id) {
        params.set('question', question.id)
      }
    }

    return params
  }

  get_cookies_array() {
    var cookies = {}
    if (typeof document === 'undefined' || !document.cookie) return cookies
    if (document.cookie === '') return cookies

    var split = document.cookie.split(';')
    for (var i = 0; i < split.length; i++) {
      var name_value = split[i].split('=')
      name_value[0] = name_value[0].replace(/^ /, '')
      cookies[decodeURIComponent(name_value[0])] = decodeURIComponent(name_value[1])
    }
    return cookies
  }

  async getAnalyticsParams() {
    if (typeof window === 'undefined') return '{}'
    const allABTests = JSON.parse(localStorage.getItem('abTests') || 'null') || {}
    const utms = JSON.parse((await storage('utms').get()) || '{}')
    const ga4_cid = this.get_cookies_array()['_ga']
    const gtm_current_attrib = this.get_cookies_array()['gtm_current_attrib']
    const gtm_first_attrib = this.get_cookies_array()['gtm_first_attrib']
    if (Object.keys(allABTests).length === 0) var output = { utm: {} }
    else
      output = {
        ab_test: { ...allABTests },
        utm: {},
        ga4_cid: '',
        gtm_current_attrib: '',
        gtm_first_attrib: '',
      }

    for (const key in utms) {
      output.utm[key.replace('utm_', '')] = utms[key]
    }
    if (Object.keys(output.utm).length === 0) {
      delete output.utm
    }
    if (ga4_cid) output.ga4_cid = ga4_cid
    if (gtm_current_attrib) output.gtm_current_attrib = gtm_current_attrib
    if (gtm_first_attrib) output.gtm_first_attrib = gtm_first_attrib

    return JSON.stringify(output)
  }

  getRequestUrl(url) {
    return import.meta.env.VITE_URL + this.parseApiVersion(url)
  }

  async getAuthorizationToken() {
    const credential = await getCredential()
    if (credential == null) {
      throw new Error('Anonymous User')
    }

    return `${credential.tokenType} ${credential.accessToken}`
  }

  async callRequest(request) {
    const url = request?.url
    const method = request?.method
    let response
    let fetchError
    try {
      response = await global.fetch(request)
    } catch (error) {
      fetchError = error
    }

    if (response && response.status >= 200 && response.status <= 210) {
      // Success response
      return response
    } else if (response) {
      // when status is 3xx, 4xx or 5xx
      throw { status: response.status, error: { ...(await response.json()) }, url, method }
    } else {
      // fetch() rejected with no HTTP response: network/CORS/DNS/timeout failure.
      // Keep the user-facing message clean, but attach the endpoint and the real
      // fetch reason so error reporting can pinpoint which request failed and why.
      throw {
        status: 500,
        error: { message: 'Server is not available' },
        url,
        method,
        reason: fetchError?.message || 'fetch failed',
        reason_name: fetchError?.name || 'FetchError',
      }
    }
  }

  async getCredential() {
    return await getCredential()
  }

  async prepareRequest(url, options = {}) {
    const requestUrl = this.getRequestUrl(url)
    const { skipAuth, ...requestInit } = options || {}
    const requestOptions = {
      mode: 'cors',
      cache: 'default',
      ...requestInit,
    }

    // Check if body needs to be serialized
    if (
      options &&
      typeof options.body === 'object' && // Array or Object
      /* eslint-disable no-prototype-builtins */
      !(global.FormData && global.FormData.prototype.isPrototypeOf(options.body)) &&
      !(global.Blob && global.Blob.prototype.isPrototypeOf(options.body))
      /* eslint-enable no-prototype-builtins */
    ) {
      //FIXME: Discover why the request works with magic,
      // and why django rest framework does not accept nested objects
      // serialized with JSON.stringify without serializing the inner object first,
      // and escaping the result string.
      if ('attribution' in options.body) {
        options.body.attribution = JSON.stringify(options.body.attribution)
      }
      requestOptions.body = JSON.stringify(options.body)
      requestOptions.headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      }
    }

    const request = new global.Request(requestUrl, requestOptions)
    request.headers.set('Accept', 'application/json')

    // Inject authorization header
    if (!skipAuth) {
      const credential = await this.getCredential()
      if (credential != null) {
        request.headers.set('Authorization', `${credential.tokenType} ${credential.accessToken}`)
      }
    }

    // Defines page language
    const language = getLocale()
    if (language) {
      request.headers.set('Accept-Language', language)
    }

    request.headers.set('analytics', await this.getAnalyticsParams())
    return request
  }
}

const instance = new RequestService()
export default instance
export { RequestService }
